import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, ArrowRight, AtSign, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

// Routes where the claim modal must NOT render even when unclaimed — these
// are public, read-only legal pages that users open in a new tab from inside
// the modal itself. The modal stays open on the original tab; closing the
// secondary tab returns them to it.
const SUPPRESS_MODAL_PATHS = new Set(['/terms', '/privacy']);

const claimSchema = z.object({
  username: z
    .string()
    .min(3, 'At least 3 characters')
    .max(24, 'At most 24 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Letters, digits, and underscores only'),
  acceptTos: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the EarthSocial Terms to continue' }),
  }),
});
type ClaimFormData = z.infer<typeof claimSchema>;

/**
 * Shown when the EarthSocial backend returns 409 `username_unclaimed` — the
 * first time a Company-authenticated user touches /api/auth/session.
 *
 * The user picks an EarthSocial-canonical handle (distinct from Company's
 * username#discriminator, which is internal-only on this platform). On
 * collision, the server returns a suggestions list we show as quick-pick
 * chips so the user can recover without retyping.
 */
export default function ClaimUsernameModal() {
  const { unclaimedCompanyUserId, claimUsername, declineClaim } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<string[]>([]);

  const form = useForm<ClaimFormData>({
    resolver: zodResolver(claimSchema),
    defaultValues: { username: '', acceptTos: false as unknown as true },
  });
  const acceptTos = form.watch('acceptTos');

  const isOpen = !!unclaimedCompanyUserId && !SUPPRESS_MODAL_PATHS.has(location.pathname);

  async function onSubmit(data: ClaimFormData) {
    setError(null);
    setSubmitting(true);
    try {
      const result = await claimUsername(data.username);
      if (result.ok) {
        // AuthContext clears unclaimedCompanyUserId on success — modal will hide.
        return;
      }
      if (result.error === 'username_taken') {
        setError('That username is taken. Try one of these:');
        setSuggested(result.suggested ?? []);
      } else {
        setError(result.error);
        setSuggested([]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function applySuggestion(s: string) {
    form.setValue('username', s);
    setError(null);
    setSuggested([]);
    if (!acceptTos) {
      // Don't submit until TOS is accepted; just stage the suggestion.
      return;
    }
    void form.handleSubmit(onSubmit)();
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] flex items-center justify-center"
          style={{ padding: '1rem' }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 rounded-2xl shadow-2xl"
            style={{
              backgroundColor: theme.cardBg,
              border: `2px solid ${theme.accentColor}40`,
              padding: '36px 28px',
              width: '100%',
              maxWidth: '440px',
            }}
          >
            <h2
              className="font-bold mb-2"
              style={{ fontSize: '24px', color: theme.textColor, textAlign: 'center' }}
            >
              Pick a username
            </h2>
            <p
              style={{
                color: theme.textColor,
                opacity: 0.75,
                fontSize: '14px',
                textAlign: 'center',
                lineHeight: 1.5,
                marginBottom: '24px',
              }}
            >
              This is how you'll appear on EarthSocial. You can change it later in Settings.
            </p>

            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="relative">
                  <AtSign
                    className="absolute"
                    style={{
                      width: '20px',
                      height: '20px',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: theme.textColor,
                      opacity: 0.5,
                    }}
                  />
                  <input
                    {...form.register('username')}
                    type="text"
                    autoComplete="username"
                    autoFocus
                    className="w-full rounded-lg focus:outline-none focus:ring-2 transition-all"
                    style={{
                      paddingLeft: '3rem',
                      paddingRight: '1rem',
                      paddingTop: '0.875rem',
                      paddingBottom: '0.875rem',
                      fontSize: '16px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: theme.textColor,
                    }}
                    placeholder="your-handle"
                  />
                </div>

                {form.formState.errors.username && (
                  <p className="text-sm" style={{ color: '#fca5a5' }}>
                    {form.formState.errors.username.message}
                  </p>
                )}

                {error && (
                  <div
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      color: '#fca5a5',
                      fontSize: '14px',
                    }}
                  >
                    {error}
                  </div>
                )}

                {suggested.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {suggested.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => applySuggestion(s)}
                        className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: `${theme.accentColor}22`,
                          border: `1px solid ${theme.accentColor}`,
                          color: theme.textColor,
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                {/* EarthSocial-specific TOS gate. Distinct from Company's TOS — covers
                    content, moderation, and marketplace rules that don't exist on Company. */}
                <label
                  className="flex items-start gap-3 cursor-pointer select-none"
                  style={{ marginTop: '4px' }}
                >
                  <input
                    {...form.register('acceptTos')}
                    type="checkbox"
                    className="sr-only peer"
                  />
                  <span
                    aria-hidden
                    className="flex-shrink-0 mt-0.5 rounded-md flex items-center justify-center transition-colors"
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: acceptTos ? theme.accentColor : 'rgba(255,255,255,0.08)',
                      border: `1.5px solid ${acceptTos ? theme.accentColor : 'rgba(255,255,255,0.3)'}`,
                    }}
                  >
                    {acceptTos && <Check className="w-3 h-3" style={{ color: '#1a1a1a' }} strokeWidth={3} />}
                  </span>
                  <span style={{ color: theme.textColor, fontSize: '13px', lineHeight: 1.5, opacity: 0.85 }}>
                    I agree to the EarthSocial{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: theme.textColor }}
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: theme.textColor }}
                    >
                      Privacy Policy
                    </Link>
                    . (Separate from Company — covers EarthSocial-specific content and moderation rules.)
                  </span>
                </label>
                {form.formState.errors.acceptTos && (
                  <p className="text-sm" style={{ color: '#fca5a5' }}>
                    {form.formState.errors.acceptTos.message}
                  </p>
                )}

                <motion.button
                  type="submit"
                  disabled={submitting || !acceptTos}
                  whileHover={{ scale: submitting ? 1 : 1.02 }}
                  whileTap={{ scale: submitting ? 1 : 0.98 }}
                  className="w-full font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{
                    padding: '14px 1rem',
                    backgroundColor: theme.accentColor,
                    color: '#1a1a1a',
                    marginTop: '8px',
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Claiming…
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>

                {/* Opt-out: keep the Company account, don't create an EarthSocial profile.
                    Suppresses the modal for this browser session; signing in again clears it. */}
                <button
                  type="button"
                  onClick={() => declineClaim()}
                  className="w-full text-center transition-opacity hover:opacity-100"
                  style={{
                    color: theme.textColor,
                    opacity: 0.6,
                    fontSize: '13px',
                    padding: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    marginTop: '4px',
                  }}
                >
                  Skip — keep my Company account only
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
